import { RecipeFilterGroup } from 'services/recipes/types';
import { RECIPE_FILTER_TOTAL_TIME } from '../constants';

/**
 * Merges the current and latest filter groups.
 *
 * - Keeps existing groups visible between API responses
 * - Sets missing option counts to `0`
 * - Appends new groups and new options
 * - Sorts values alphabetically, except for `Total Time`
 *
 * @param previousFilters The filter groups currently shown in the UI
 * @param nextFilters The filter groups returned by the latest API response
 * @returns A merged list of filter groups
 */
export const mergeFilters = (
  previousFilters: RecipeFilterGroup[] = [],
  nextFilters: RecipeFilterGroup[] = [],
): RecipeFilterGroup[] => {
  if (!previousFilters.length) {
    return nextFilters;
  }

  const mergedFilters = previousFilters.map(previousGroup => {
    // Find the matching group from the latest API response
    const updatedGroup = nextFilters.find(group => group.title === previousGroup.title);

    const mergedValues = previousGroup.values.map(previousValue => {
      // Update the count if the value still exists, otherwise keep it with count 0
      const updatedValue = updatedGroup?.values.find(value => value.name === previousValue.name);

      return {
        ...previousValue,
        count: updatedValue?.count ?? 0,
      };
    });

    // Collect existing value names so we only add values that are new
    const previousValueNames = new Set(previousGroup.values.map(value => value.name));

    const newValues =
      updatedGroup?.values.filter(updatedValue => !previousValueNames.has(updatedValue.name)) ?? [];

    const values = [...mergedValues, ...newValues];

    return {
      ...previousGroup,
      // Keep Total Time order as-is; sort the other groups alphabetically
      values:
        previousGroup.title !== RECIPE_FILTER_TOTAL_TIME
          ? [...values].sort((a, b) => a.name.localeCompare(b.name))
          : values,
    };
  });

  const newGroups = nextFilters.filter(nextGroup => {
    return !previousFilters.some(previousGroup => previousGroup.title === nextGroup.title);
  });

  return [...mergedFilters, ...newGroups];
};

export default mergeFilters;
